'use client'
import MultiBeforeAndAfterImage from "@/components/ui/cardSection/MultiBeforeAndAfterImage";
import type { GalleryBeforeAndAfterNormalDataEntity } from "@/types/GetGalleryBeforeAndAfterApiType";
import { Button, Tab, Tabs } from "@heroui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface GalleryTabsIndexProps {
    galleryData?: GalleryBeforeAndAfterNormalDataEntity[] | null;
    treatmentName?: string;
}

const GALLERY_PAGE_SIZE = 12;

type GalleryGridItem = {
    _id: string;
    beforeImage: string;
    alt_name: string;
    title: string;
    description: string;
};

function GalleryTabPanel({
    items,
    treatmentId,
}: {
    items: GalleryGridItem[];
    treatmentId?: string;
}) {
    const [visibleCount, setVisibleCount] = useState(GALLERY_PAGE_SIZE);
    const shown = useMemo(() => items.slice(0, visibleCount), [items, visibleCount]);
    const hasMore = visibleCount < items.length;

    useEffect(() => {
        setVisibleCount(GALLERY_PAGE_SIZE);
    }, [items]);

    const loadMore = useCallback(() => {
        setVisibleCount((n) => Math.min(n + GALLERY_PAGE_SIZE, items.length));
    }, [items.length]);

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 lg:gap-y-10">
                {shown.map((data) => (
                    <div
                        className="col-span-1"
                        key={`slider_${treatmentId ?? "tab"}_${data._id}`}
                    >
                        <MultiBeforeAndAfterImage
                            image_url={data.beforeImage}
                            alt_name={data.alt_name}
                        />
                        <div className="p-4">
                            <p className="text-textf2 font-medium text-sm lg:text-[17.9px]/[27px]">{data?.title}</p>
                            <p className="text-textf1 font-bold text-lg lg:text-[23.1px]/[27px]">{data?.description}</p>
                        </div>
                    </div>
                ))}
            </div>
            {hasMore ? (
                <div className="flex justify-center mt-1">
                    <Button
                        type="button"
                        variant="bordered"
                        className="min-w-[160px] rounded-full border-lightBrown text-lightBrown font-medium"
                        onPress={loadMore}
                    >
                        Load more
                    </Button>
                </div>
            ) : null}
        </>
    );
}

const GalleryTabsIndex = ({ galleryData, treatmentName }: GalleryTabsIndexProps) => {
    const tabItems = useMemo(
        () =>
            galleryData?.map((curr, tabIndex) => {
                const items: GalleryGridItem[] =
                    curr?.beforeAfters
                        ?.filter((item) => item?.galleryImages?.[0]?.url)
                        ?.map((item) => ({
                            _id: item._id,
                            beforeImage: item.galleryImages![0].url,
                            alt_name:
                                item.galleryImages![0].alt ??
                                item.galleryImages![0].fileName ??
                                "images_before_after",
                            title: item.title,
                            description: item.description,
                        })) ?? [];

                const treatmentId = curr?.treatment?._id;

                return {
                    key: treatmentId ?? `before_after_${tabIndex + 1}`,
                    title: curr?.treatment?.name ?? "No Tag",
                    content: <GalleryTabPanel items={items} treatmentId={treatmentId} />,
                };
            }) ?? [],
        [galleryData]
    );

    const defaultSelectedKey = useMemo(() => {
        if (!treatmentName || tabItems.length === 0) return tabItems[0]?.key;
        const matching = tabItems.find((item) => item.title === treatmentName);
        return matching?.key ?? tabItems[0]?.key;
    }, [treatmentName, tabItems]);

    const [selectedKey, setSelectedKey] = useState<string | undefined>(defaultSelectedKey);
    const tabsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setSelectedKey(defaultSelectedKey);
    }, [defaultSelectedKey]);

    useEffect(() => {
        if (!treatmentName || !defaultSelectedKey || !tabsRef.current) return;
        const tabIndex = tabItems.findIndex((item) => item.key === defaultSelectedKey);
        if (tabIndex < 0) return;
        const timer = setTimeout(() => {
            const tablist = tabsRef.current?.querySelector('[role="tablist"]');
            const tabs = tablist?.querySelectorAll('[role="tab"]');
            const targetTab = tabs?.[tabIndex];
            if (targetTab) {
                targetTab.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [treatmentName, defaultSelectedKey, tabItems]);

    useEffect(() => {
        const root = tabsRef.current;
        if (!root) return;

        const tabList =
            root.querySelector<HTMLElement>('[data-slot="tabList"]') ??
            root.querySelector<HTMLElement>('[role="tablist"]');
        if (!tabList) return;

        const onWheel = (e: WheelEvent) => {
            if (tabList.scrollWidth <= tabList.clientWidth + 2) return;

            const dy = e.deltaY;
            const dx = e.deltaX;
            if (Math.abs(dx) > Math.abs(dy)) return;
            if (dy === 0) return;

            const maxScroll = tabList.scrollWidth - tabList.clientWidth;
            const nextLeft = tabList.scrollLeft + dy;
            const clamped = Math.max(0, Math.min(maxScroll, nextLeft));

            if (clamped !== tabList.scrollLeft) {
                e.preventDefault();
                tabList.scrollLeft = clamped;
            }
        };

        const DRAG_THRESHOLD_PX = 8;
        let dragPointerId: number | null = null;
        let dragStartX = 0;
        let dragStartScroll = 0;
        let dragMoved = false;

        const onPointerDown = (e: PointerEvent) => {
            if (e.pointerType === "touch") return;
            if (e.button !== 0) return;
            if (tabList.scrollWidth <= tabList.clientWidth + 2) return;

            dragPointerId = e.pointerId;
            dragStartX = e.clientX;
            dragStartScroll = tabList.scrollLeft;
            dragMoved = false;
            // Do not setPointerCapture here — immediate capture on the tab list
            // breaks click delivery to tab buttons (pointerup is retargeted away from the tab).
        };

        const onPointerMove = (e: PointerEvent) => {
            if (dragPointerId === null || e.pointerId !== dragPointerId) return;
            const dx = e.clientX - dragStartX;
            if (Math.abs(dx) <= DRAG_THRESHOLD_PX) return;

            if (!dragMoved) {
                dragMoved = true;
                try {
                    tabList.setPointerCapture(e.pointerId);
                } catch {
                    /* ignore */
                }
            }
            tabList.scrollLeft = dragStartScroll - dx;
        };

        const endPointerDrag = (e: PointerEvent) => {
            if (dragPointerId === null || e.pointerId !== dragPointerId) return;
            try {
                tabList.releasePointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
            dragPointerId = null;

            if (dragMoved) {
                const suppressClick = (ev: MouseEvent) => {
                    ev.preventDefault();
                    ev.stopImmediatePropagation();
                };
                tabList.addEventListener("click", suppressClick, true);
                window.setTimeout(() => {
                    tabList.removeEventListener("click", suppressClick, true);
                }, 0);
            }
            dragMoved = false;
        };

        tabList.addEventListener("wheel", onWheel, { passive: false });
        tabList.addEventListener("pointerdown", onPointerDown);
        tabList.addEventListener("pointermove", onPointerMove);
        tabList.addEventListener("pointerup", endPointerDrag);
        tabList.addEventListener("pointercancel", endPointerDrag);

        return () => {
            tabList.removeEventListener("wheel", onWheel);
            tabList.removeEventListener("pointerdown", onPointerDown);
            tabList.removeEventListener("pointermove", onPointerMove);
            tabList.removeEventListener("pointerup", endPointerDrag);
            tabList.removeEventListener("pointercancel", endPointerDrag);
        };
    }, [tabItems.length]);

    return (
        <div ref={tabsRef}>
            <Tabs
                key="light"
                aria-label="Tabs variants"
                variant="light"
                selectedKey={selectedKey ?? defaultSelectedKey}
                onSelectionChange={(key) => setSelectedKey(key as string)}
                className="hero_tabs_parent"
                classNames={{
                    tab: "hero_single_tab",
                    tabContent: "hero_tab_content",
                    cursor: "hero_tab_cursor",
                    tabList:
                        "w-full max-w-full flex-nowrap overflow-x-auto overflow-y-hidden touch-pan-x cursor-grab active:cursor-grabbing select-none",
                }}
                items={tabItems}
                disableAnimation={false}
                disableCursorAnimation={false}
            >
                {(item) => (
                    <Tab key={item.key} title={item.title}>
                        {item?.content}
                    </Tab>
                )}
            </Tabs>
        </div>
    )
}

export default GalleryTabsIndex
